import React, { useState } from 'react';
import { LayoutDashboard, Music, Users, Radio, Inbox, BarChart3, Shield, ArrowRight, CheckCircle2 } from 'lucide-react';

const STEPS = [
  {
    icon: LayoutDashboard,
    title_es: 'Bienvenido al centro de control',
    title_en: 'Welcome to the control center',
    desc_es: 'Este es tu panel de operaciones. Desde aqui gestionas todo lo que ocurre en PulseMusic: artistas, canciones, eventos, usuarios, bookings, live streaming y mas. Vamos a recorrerlo juntos.',
    desc_en: 'This is your operations panel. From here you manage everything happening on PulseMusic: artists, songs, events, users, bookings, live streaming and more. Let\'s walk through it together.',
  },
  {
    icon: Music,
    title_es: 'Artistas, canciones y eventos',
    title_en: 'Artists, songs and events',
    desc_es: 'En las pestanas Artistas, Canciones y Eventos puedes crear, editar y eliminar contenido. Cada artista tiene un perfil con foto, biografia, generos y precio por show. Las canciones pasan por un proceso de verificacion antes de publicarse.',
    desc_en: 'In the Artists, Songs and Events tabs you can create, edit and delete content. Each artist has a profile with photo, bio, genres and show price. Songs go through a verification process before publishing.',
  },
  {
    icon: Users,
    title_es: 'Gestion de usuarios',
    title_en: 'User management',
    desc_es: 'En Cuentas puedes ver todos los usuarios, cambiar roles, suspender cuentas y gestionar permisos. Los roles son: usuario (oyente), artista, moderador, pluka (tu) y admin. Cada rol tiene accesos diferentes.',
    desc_en: 'In Accounts you can see all users, change roles, suspend accounts and manage permissions. Roles are: user (listener), artist, moderator, pluka (you) and admin. Each role has different access levels.',
  },
  {
    icon: Inbox,
    title_es: 'Operaciones (tu base)',
    title_en: 'Operations (your home base)',
    desc_es: 'La pestana Operaciones es tu centro de trabajo principal. Aqui encuentras mensajes de artistas, chats internos, gestion de eventos y tareas pendientes. Piensa en ella como tu bandeja de entrada profesional.',
    desc_en: 'The Operations tab is your main workspace. Here you find artist messages, internal chats, event management and pending tasks. Think of it as your professional inbox.',
  },
  {
    icon: Radio,
    title_es: 'Control de Lives',
    title_en: 'Live control',
    desc_es: 'En Control Live tienes poder total sobre las emisiones en directo. Puedes ver todas las sesiones activas, moderar chat, banear usuarios, terminar emisiones y revisar estadisticas. Es el "god mode" de los directos.',
    desc_en: 'In Live Control you have full power over live streams. You can see all active sessions, moderate chat, ban users, end streams and review stats. It\'s the "god mode" for live.',
  },
  {
    icon: BarChart3,
    title_es: 'Metricas y datos',
    title_en: 'Metrics and data',
    desc_es: 'Las pestanas de Finanzas, Usuarios Intel y Contenido te dan vision completa de como va la plataforma. Reproducciones, apoyos, tendencias, artistas top y comportamiento de usuarios.',
    desc_en: 'The Finance, Users Intel and Content tabs give you a complete view of platform performance. Plays, supports, trends, top artists and user behavior.',
  },
  {
    icon: Shield,
    title_es: 'Seguridad y responsabilidad',
    title_en: 'Security and responsibility',
    desc_es: 'Todas tus acciones quedan registradas en el log de auditoria. Trata los datos de los usuarios con respeto y confidencialidad. Si tienes dudas sobre una decision, consulta con la directora antes de actuar. Este panel es una herramienta de trabajo, no un juguete.',
    desc_en: 'All your actions are logged in the audit trail. Treat user data with respect and confidentiality. If unsure about a decision, consult with the director before acting. This panel is a work tool, not a toy.',
  },
];

export default function AdminOnboarding({ lang, onComplete }) {
  const [step, setStep] = useState(0);
  const isLast = step >= STEPS.length - 1;
  const current = STEPS[step];

  const handleFinish = () => {
    localStorage.setItem('pulse_admin_onboarding_done', 'true');
    onComplete?.();
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0A0A0B] border border-[#27272A] rounded-2xl w-full max-w-lg">
        <div className="flex gap-1 p-4 pb-0">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? 'bg-[#F5C842]' : 'bg-[#27272A]'}`} />
          ))}
        </div>

        <div className="p-6">
          <div className="w-12 h-12 rounded-xl bg-[#F5C842]/10 border border-[#F5C842]/30 flex items-center justify-center mb-4">
            <current.icon size={24} className="text-[#F5C842]" />
          </div>
          <h2 className="font-heading font-bold text-lg mb-3">
            {lang === 'es' ? current.title_es : current.title_en}
          </h2>
          <p className="text-sm text-pulse-muted leading-relaxed mb-6">
            {lang === 'es' ? current.desc_es : current.desc_en}
          </p>
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-pulse-muted">{step + 1} / {STEPS.length}</span>
            <button
              onClick={() => isLast ? handleFinish() : setStep(step + 1)}
              className="flex items-center gap-2 bg-[#F5C842] text-black px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-[#F5C842]/90 transition-colors"
            >
              {isLast ? (lang === 'es' ? 'Empezar a trabajar' : 'Start working') : (lang === 'es' ? 'Siguiente' : 'Next')}
              {isLast ? <CheckCircle2 size={14} /> : <ArrowRight size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
